<template>
  <v-card elevation="10" min-width="500" max-width="680">
    <v-card-text ref="report">
      <v-layout row wrap>
        <v-flex md12 lg4>
          <div class="riparian-assessment-container">
            <div class="riparian-assessment-head1">
              RIPARIAN
            </div>
            <div class="riparian-assessment-head2">
              ASSESSMENT
            </div>
          </div>
          <div class="description-text">
            Riparian areas are the biologically rich and productive shoreline
            areas at the edges of lakes, streams, wetlands, and rivers.
          </div>
          <div class="description-text" style="padding-bottom: 5px;">
            A GIS-based method and dataset was created to assess riparian
            intactness (or condition) at the watershed scale, including
            lakeshores and streams. The riparian area was assessed 50 metres
            back from the water line and given an intactness rating based on
            what is visible from satellite imagery.
          </div>
          <div class="rating-box">
            <div class="rating-heading-box">INTACTNESS RATING</div>
            <v-layout row wrap style="padding: 5px;">
              <v-flex sm3>
                <div
                  class="legend-color"
                  style="background-color: rgb(141, 198, 63);"
                >
                  HIGH
                </div>
              </v-flex>
              <v-flex sm3>
                <div class="legend-text">
                  Vegetation present. Little or no human footprint.
                </div>
              </v-flex>
              <v-flex sm3>
                <div
                  class="legend-color"
                  style="background-color: rgb(248, 151, 40);"
                >
                  LOW
                </div>
              </v-flex>
              <v-flex sm3>
                <div class="legend-text">
                  Vegetation present. Human footprint prevalent.
                </div>
              </v-flex>
              <v-flex sm3>
                <div
                  class="legend-color"
                  style="background-color: rgb(255, 207, 1);"
                >
                  MODERATE
                </div>
              </v-flex>
              <v-flex sm3>
                <div class="legend-text">
                  Vegetation present. Some human footprint.
                </div>
              </v-flex>
              <v-flex sm3>
                <div
                  class="legend-color"
                  style="background-color: rgb(208, 36, 43);"
                >
                  VERY LOW
                </div>
              </v-flex>
              <v-flex sm3>
                <div class="legend-text">
                  Vegetation mostly clear. Human footprint dominant.
                </div>
              </v-flex>
            </v-layout>
          </div>

          <div style="padding-top: 10px;">
            <div class="summary-box">
              <div>
                <div class="summary-box-title">
                  Summary of Results
                </div>
                <div class="summary-box-subtitle">
                  {{ dataYear ? `Data Year: ${dataYear}` : null }}
                </div>
              </div>
              <div class="description-text">{{ summaryText }}</div>
            </div>
          </div>
        </v-flex>
        <v-flex md12 lg8>
          <v-layout row align-center>
            <v-flex sm2>
              <v-img
                :src="require('../assets/images/logo_notext.png')"
                contain
              />
            </v-flex>
            <v-flex sm10>
              <div class="study-area-title">
                {{ studyAreaName }}
              </div>
            </v-flex>
          </v-layout>
          <div style="padding-top: 10px;">
            <v-layout row align-center justify-center>
              <v-flex sm2>
                <div class="summary-circle">
                  <div class="assessed-length-text">
                    {{ assessedLength }}
                  </div>
                  <div class="assessed-length-subtext">
                    Kilometres Assessed
                  </div>
                </div>

                <div class="summary-circle">
                  <div
                    class="assessed-length-text"
                    style="color: rgb(141, 198, 63)"
                  >
                    {{ high }}%
                  </div>
                  <div class="assessed-length-subtext">
                    High Intactness
                  </div>
                </div>
              </v-flex>
              <v-flex sm10>
                <div class="chart-container">
                  <apexchart
                    type="bar"
                    width="340"
                    height="90"
                    :options="chartOptions"
                    :series="series"
                  ></apexchart>
                </div>
              </v-flex>
            </v-layout>
          </div>
          <div class="map-container">
            <div id="reportmap" class="map">
              <!-- <div id="insetmap" class="inset-map"></div> -->
            </div>
          </div>
        </v-flex>
      </v-layout>
    </v-card-text>
    <v-card-actions>
      <div class="action-buttons">
        <v-btn flat color="warning" @click="close()">close</v-btn>
        <v-btn flat color="success" @click="saveReport()">save</v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import * as mapboxgl from "mapbox-gl";
import VueApexCharts from "vue-apexcharts";
import html2canvas from "html2canvas";

export default {
  name: "ReportCard",

  components: {
    apexchart: VueApexCharts,
  },

  props: {
    data: Object,
    insetGeo: Object,
    baseMap: Object,
  },

  data() {
    return {
      // Chart data
      mockSeries: [
        {
          name: "Very Low Intactness",
          data: [34946274.42],
          color: "rgb(208, 36, 43)",
        },
        {
          name: "Low Intactness",
          data: [28368163.05],
          color: "rgb(248, 151, 40)",
        },
        {
          name: "Moderate Intactness",
          data: [21490450.58],
          color: "rgb(255, 207, 1)",
        },
        {
          name: "High Intactness",
          data: [21490450.58],
          color: "rgb(141, 198, 63)",
        },
      ],
      baseLayer: null,
    };
  },

  computed: {
    mapOptions() {
      return {
        container: "reportmap",
        style: this.baseLayer.value,
        center: [-114, 55],
        zoom: 5,
        maxZoom: 12,
      };
    },
    series() {
      if (this.data.series) {
        const series = [...this.mockSeries];

        series.forEach((p, i) => {
          p.data = [this.data.series[i]];
        });

        return series;
      } else return this.mockSeries;
    },
    geo() {
      return this.data.geo ? this.data.geo : {};
    },
    studyAreaName() {
      return this.data.geo.properties.NAME;
    },
    dataYear() {
      return null;
    },
    seriesSum() {
      return this.data.series.reduce((val1, val2) => {
        return val1 + val2;
      });
    },
    assessedLengthFull() {
      let length = this.data.series.reduce((val1, val2) => {
        return val1 + val2;
      });

      return Math.round(length);
    },
    assessedLength() {
      let length = this.data.series.reduce((val1, val2) => {
        return val1 + val2;
      });

      if (length > 999) length = `${Math.round(length / 100) / 10}k`;
      else length = Math.round(length * 10) / 10;

      return length;
    },
    high() {
      return this.roundPerc((this.data.series[3] / this.seriesSum) * 100);
    },
    moderate() {
      return this.roundPerc((this.data.series[2] / this.seriesSum) * 100);
    },
    low() {
      return this.roundPerc((this.data.series[1] / this.seriesSum) * 100);
    },
    veryLow() {
      return this.roundPerc((this.data.series[0] / this.seriesSum) * 100);
    },
    summaryText() {
      return `Out of the ${this.assessedLengthFull} km of shoreline assessed, ${this.high}%
      was considered to have high intactness, ${this.moderate}% was moderate, ${this.low}%
      was low, and ${this.veryLow}% was very low.`;
    },
    chartOptions() {
      return {
        chart: {
          width: 380,
          type: "bar",
          stacked: true,
          stackType: "100%",
          background: "rgba(0, 0, 0, 0)",
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: false,
          formatter: (val, { seriesIndex }) => {
            return `${(Math.round(this.series[seriesIndex].data[0] * 10) / 10)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} km`;
          },
        },
        yaxis: {
          labels: {
            show: false,
          },
        },
        xaxis: {
          categories: ["Intactness"],
          labels: {
            style: {
              colors: ["#595959"],
            },
            formatter: (val) => {
              return `${val}%`;
            },
          },
          // title: {
          //   text: "Proportion (%) of Shoreline Length Assessed",
          //   style: {
          //     color: "#595959",
          //   },
          // },
        },
        grid: {
          show: false,
        },
        legend: {
          show: false,
          position: "top",
          labels: {
            colors: "#595959",
          },
        },
        theme: {
          mode: "light",
        },
      };
    },
  },

  watch: {
    baseMap(layer) {
      if (layer.name !== this.baseLayer) {
        this.baseLayer = layer.name;
        this.map.setStyle(layer.value);
      }
    },
  },

  methods: {
    roundPerc(val) {
      return Math.round(val * 10) / 10;
    },
    initMap() {
      const loadLayers = (map, geo) => {
        let firstSymbolId;
        let layers = map.getStyle().layers;
        for (let i = 0; i < layers.length; i++) {
          if (layers[i].type === "symbol") {
            firstSymbolId = layers[i].id;
            break;
          }
        }

        // Add a hillshade
        map.addSource("dem", {
          type: "raster-dem",
          url: "mapbox://mapbox.terrain-rgb",
        });
        map.addLayer(
          {
            id: "dem-hillshade",
            source: "dem",
            type: "hillshade",
            maxzoom: 13,
          },
          firstSymbolId
        );

        // Add intactness
        map.addSource("intactness", {
          type: "vector",
          tiles: [
            "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/riparian/intactness/{z}/{x}/{y}.pbf",
          ],
        });

        const pallette = ["#f54242", "#eba834", "#daed09", "#45f542"];
        const options = [
          "Very Low",
          "Very Low Intactness",
          "Low",
          "Low Intactness",
          "Moderate",
          "Moderate Intactness",
          "High",
          "High Intactness",
        ];
        let expression = ["match", ["get", "Intactness"]];

        let optI = 0;
        for (let i = 0; i < pallette.length; i++) {
          expression.push(options[optI], pallette[i]);
          optI += 2;
        }
        optI = 1;
        for (let i = 0; i < pallette.length; i++) {
          expression.push(options[optI], pallette[i]);
          optI += 2;
        }
        expression.push("rgba(0,0,0,0)");

        map.addLayer(
          {
            id: "intactness",
            type: "fill",
            source: "intactness",
            "source-layer": "intactness",
            layout: {},
            paint: { "fill-opacity": 0.5, "fill-color": expression },
            minzoom: 7,
          },
          firstSymbolId
        );

        // Add study area
        map.addSource("studyArea", {
          type: "geojson",
          data: geo,
        });
        map.addLayer(
          {
            id: "studyArea",
            source: "studyArea",
            type: "line",
            paint: {
              "line-color": "#595959",
              "line-width": 2,
            },
          },
          firstSymbolId
        );

        // Zoom to layer
        let coordinates = [];

        const nextCoords = (coords) => {
          coords.forEach((coord) => {
            if (isNaN(coord[0])) {
              nextCoords(coord);
            } else coordinates.push(coord);
          });
        };

        nextCoords(this.data.geo.geometry.coordinates);

        // Create a 'LngLatBounds' with both corners at the first coordinate.
        const bounds = new mapboxgl.LngLatBounds(
          coordinates[0],
          coordinates[0]
        );

        // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
        for (const coord of coordinates) {
          bounds.extend(coord);
        }

        map.fitBounds(bounds, {
          padding: 30,
        });
      };

      mapboxgl.accessToken = this.$store.state.map.mapbox_gl_token;
      this.map = new mapboxgl.Map(this.mapOptions);

      this.map.on("load", () => {
        loadLayers(this.map, this.data.geo);
      });

      this.map.on("style.load", () => {
        loadLayers(this.map, this.data.geo);
      });
    },

    async saveReport() {
      const el = this.$refs.report;

      const options = {
        type: "dataURL",
      };
      const printCanvas = await html2canvas(el, options);

      const link = document.createElement("a");
      link.setAttribute("download", `${this.studyAreaName}.png`);
      link.setAttribute(
        "href",
        printCanvas
          .toDataURL("image/png")
          .replace("image/png", "image/octet-stream")
      );
      link.click();
    },
    close() {
      this.$emit("close");
    },
  },

  beforeMount() {
    this.baseLayer = this.baseMap;

    HTMLCanvasElement.prototype.getContext = (function(origFn) {
      return function(type, attribs) {
        attribs = attribs || {};
        attribs.preserveDrawingBuffer = true;
        return origFn.call(this, type, attribs);
      };
    })(HTMLCanvasElement.prototype.getContext);
  },

  mounted() {
    this.initMap();
  },
};
</script>

<style scoped>
.riparian-assessment-container {
  background-color: #34b4eb;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}
.riparian-assessment-head1 {
  color: white;
  letter-spacing: 4px;
  font-size: 33px;
  font-weight: bold;
  text-align: center;
  padding-bottom: 10px;
}
.riparian-assessment-head2 {
  color: white;
  font-size: 27px;
  font-weight: bold;
  text-align: center;
}
.description-text {
  font-size: 10px;
  color: #595959;
  text-align: justify;
  padding-top: 5px;
  line-height: normal;
}
.study-area-title {
  color: #34b4eb;
  font-size: 30px;
  text-align: center;
  font-weight: bold;
  vertical-align: center;
  line-height: 30px;
}
.rating-box {
  border: solid;
  border-color: #595959;
  border-width: 1px;
}
.rating-heading-box {
  background-color: #595959;
  color: white;
  text-align: center;
  font-weight: bold;
}
.legend-color {
  height: 40px;
  width: 40px;
  border-radius: 50%;
  display: inline-block;
  color: white;
  font-size: 7px;
  text-align: center;
  font-weight: bold;
  vertical-align: middle;
  line-height: 40px;
}
.legend-text {
  font-size: 8px;
  color: #595959;
  padding-top: 2px;
  line-height: normal;
}
.summary-box {
  border: solid;
  border-color: #34b4eb;
  border-width: 1px;
  padding: 5px;
}
.summary-box-title {
  color: #34b4eb;
  font-size: 12px;
  font-weight: bold;
}
.summary-box-subtitle {
  color: #595959;
  font-size: 10px;
  font-weight: bold;
}
.chart-container {
  border: solid;
  border-color: #34b4eb;
  border-width: 1px;
}
.summary-circle {
  height: 55px;
  width: 55px;
  border: solid;
  border-radius: 50%;
  border-color: #34b4eb;
  margin-left: auto;
  margin-right: auto;
  padding-top: 6px;
}
.assessed-length-text {
  color: #34b4eb;
  font-size: 15px;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
}
.assessed-length-subtext {
  color: #595959;
  font-size: 8px;
  text-align: center;
  vertical-align: middle;
  line-height: 10px;
}
.map-container {
  padding-top: 10px;
  padding-left: 10px;
  height: 250px;
  width: 100%;
}
.map {
  padding: 0;
  position: relative;
  height: 100%;
  width: 100%;
}
.inset-map {
  border: solid;
  border-color: #595959;
  border-width: 1px;
  padding: 0;
  position: relative;
  bottom: 2px;
  left: 2px;
  height: 25%;
  width: 25%;
  z-index: 2;
}
.action-buttons {
  width: 100%;
  text-align: center;
}
</style>
